import {FC, useEffect, useMemo} from 'react';
//mui
import {toast} from 'react-toastify';
import {Box, useMediaQuery} from '@mui/material';
//store
import {useAppSelector, useAppDispatch} from '@/store/hooks';
import {clearTableTicketsData, getTableTickets, getTicketsTableSelector} from '@/store/organization-tables';
//services
import {OrganizationTableAndTicketsService} from '@/utils/services';
//formik
import {Formik} from 'formik';
import {initialValues, validationSchema} from './formConfiguration';
//helpers
import {getDefaultRequestBody, getErrorMessage} from '@/utils/helpers';
import {getEditTableDetailsValues} from './helpers';
//components
import FormContent from './FormContent';
//types
import {IOrganizationTableTicketsRequest, IOrganizationTableTicketsResponse} from '@/utils/types';
//styles
import {tableDetailsStyle} from './TableDetails.styles';
import {defaultPageSize, EVENT_ID} from '@/utils/constants';
import {getCurrentEventSelector} from '@/store/organization-event';
import {getAttendeesList, getAttendeesStatistics} from '@/store/organization-attendees';
import {useApiInstance} from '@/contexts/ApiInstanceContext';

interface TableDetailsProps {
	currentTableId: number | undefined;
	onCloseModal: () => void;
	onSuccess?: () => void;
}

const TableDetails: FC<TableDetailsProps> = ({currentTableId, onCloseModal, onSuccess}) => {
	const dispatch = useAppDispatch();

	const ticketsTable = useAppSelector(getTicketsTableSelector);
	const currentEvent = useAppSelector(getCurrentEventSelector);

	const isLaptop = useMediaQuery('(min-width:1200px)');

	const {apiInstance} = useApiInstance();

	useEffect(() => {
		if (currentTableId) getTableTicketsData();
	}, [currentTableId]);

	useEffect(() => {
		return () => {
			dispatch(clearTableTicketsData());
		};
	}, [dispatch]);

	const getTableTicketsData = async () => {
		try {
			await dispatch(getTableTickets({reqBody: currentTableId as number, apiInstance})).unwrap();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const formikInitialValues = useMemo(() => {
		if (ticketsTable) return ticketsTable;
		return initialValues;
	}, [ticketsTable]);

	const handleSubmitClickHandler = async (values: IOrganizationTableTicketsResponse) => {
		if (!currentEvent?.id) return;

		try {
			const reqBody = getDefaultRequestBody(currentEvent.id, EVENT_ID);

			const editValues: IOrganizationTableTicketsRequest = getEditTableDetailsValues(values);

			await OrganizationTableAndTicketsService.updateTableTickets(values.id, editValues, apiInstance);
			toast.success('Table information updated successfully');

			await dispatch(getAttendeesList({...reqBody, pageSize: isLaptop ? defaultPageSize : 10000})).unwrap();
			await dispatch(getAttendeesStatistics(currentEvent.id)).unwrap();

			onSuccess && onSuccess();

			onCloseModal();
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	return (
		<Box sx={tableDetailsStyle.wrapper}>
			<Formik
				enableReinitialize
				initialValues={formikInitialValues}
				onSubmit={handleSubmitClickHandler}
				validationSchema={validationSchema}
			>
				<FormContent handleCloseModal={onCloseModal} />
			</Formik>
		</Box>
	);
};

export default TableDetails;
